/**
 * Quick Edit Modal
 * Opens automatically after VST upload for fast tag editing
 * Uses file store updateFile so the list updates without reload.
 */

import React, { useState, useEffect } from 'react';
import { X, Tag as TagIcon, Save } from 'lucide-react';
import { AudioFile } from '@/types';
import { supabase } from '@/config/supabase';
import { useToast } from '@/hooks';
import { useAudioStore } from '@stores/useAudioStore';
import { useFileStore } from '@stores/useFileStore';
import { showSuspensionToastIfNeeded } from '@utils/suspension';

interface QuickEditModalProps {
  file: AudioFile;
  onClose: () => void;
  onSave: () => void;
}

export const QuickEditModal: React.FC<QuickEditModalProps> = ({
  file,
  onClose,
  onSave,
}) => {
  const [tagInput, setTagInput] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>(
    file.tags?.map(t => t.label) || []
  );
  const [availableTags, setAvailableTags] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { success, error: showError } = useToast();
  const { stop } = useAudioStore();
  const { updateFile } = useFileStore();
  
  // Stop player when modal opens
  useEffect(() => {
    stop();
  }, [stop]);

  // Load available tags
  useEffect(() => {
    const loadTags = async () => {
      const { data } = await supabase
        .from('tags')
        .select('*')
        .order('label');
      
      if (data) {
        setAvailableTags(data);
      }
    };
    loadTags();
  }, []);

  const handleAddTag = (tagLabel: string) => {
    if (!selectedTags.includes(tagLabel)) {
      setSelectedTags([...selectedTags, tagLabel]);
    }
    setTagInput('');
  };

  const handleRemoveTag = (tagLabel: string) => {
    setSelectedTags(selectedTags.filter(t => t !== tagLabel));
  };

  const handleSave = async () => {
    // Check suspension FIRST before any operations
    const isSuspended = await showSuspensionToastIfNeeded(showError);
    if (isSuspended) {
      return;
    }
    
    setIsLoading(true);
    try {
      // Resolve selected tag labels to tag IDs (create new tags in DB if needed)
      const tagIds: string[] = [];
      
      for (const tagLabel of selectedTags) {
        const existingTag = availableTags.find(t => t.label === tagLabel);
        
        if (existingTag) {
          tagIds.push(existingTag.id);
        } else {
          const { data: newTag } = await supabase
            .from('tags')
            .insert({
              label: tagLabel,
              type: 'custom',
              color: '#888888',
            })
            .select()
            .single();
          
          if (newTag) {
            tagIds.push(newTag.id);
          }
        }
      }

      // Use store updateFile so the file list updates without reload (merge into state)
      await updateFile(file.id, { tags: tagIds as unknown as AudioFile['tags'] });

      success('Tags updated!', 'Your tags have been saved.');
      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to update tags:', err);
      showError('Failed to update tags');
    } finally {
      setIsLoading(false);
    }
  };

  const suggestedTags = availableTags
    .filter(t => !selectedTags.includes(t.label))
    .slice(0, 10);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4">
      <div className="bg-dark-secondary/95 backdrop-blur-lg border border-white/10 rounded-xl shadow-2xl p-4 max-w-lg w-full animate-slide-in-up">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0 pr-2">
            <h2 className="font-dela text-base text-white flex items-center gap-2">
              <TagIcon className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="truncate">Quick Tag Edit</span>
            </h2>
            <p className="font-inter text-xs text-white/60 mt-1 truncate">
              Just uploaded: <span className="text-primary">{file.name}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all flex-shrink-0"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Selected Tags */}
        <div className="mb-3">
          <label className="block font-inter text-xs text-white/60 mb-1.5">
            Current Tags
          </label>
          <div className="flex flex-wrap gap-1.5 min-h-[36px] p-2 bg-dark/40 rounded-lg border border-white/5">
            {selectedTags.length === 0 ? (
              <span className="text-white/30 text-xs">No tags yet...</span>
            ) : (
              selectedTags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 bg-primary/20 text-primary rounded-full font-inter text-xs flex items-center gap-1.5 group"
                >
                  <span className="truncate max-w-[120px]">{tag}</span>
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="hover:text-red-400 transition-colors flex-shrink-0"
                  >
                    ×
                  </button>
                </span>
              ))
            )}
          </div>
        </div>

        {/* Tag Input */}
        <div className="mb-3">
          <label className="block font-inter text-xs text-white/60 mb-1.5">
            Add New Tag
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && tagInput.trim()) {
                  handleAddTag(tagInput.trim());
                }
              }}
              placeholder="Type and press Enter..."
              className="flex-1 px-3 py-1.5 text-sm bg-dark/60 text-white rounded-lg border border-white/10 focus:outline-none focus:border-primary transition-all font-inter"
            />
          </div>
        </div>

        {/* Suggested Tags */}
        {suggestedTags.length > 0 && (
          <div className="mb-4">
            <label className="block font-inter text-xs text-white/60 mb-1.5">
              Suggested Tags
            </label>
            <div className="flex flex-wrap gap-1.5">
              {suggestedTags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleAddTag(tag.label)}
                  className="px-2 py-1 bg-white/10 hover:bg-primary/20 text-white/80 hover:text-primary rounded-full font-inter text-xs transition-all"
                >
                  + {tag.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-2 text-sm bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all font-inter font-bold"
          >
            Skip
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="flex-1 px-3 py-2 text-sm bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-black rounded-lg transition-all font-inter font-bold shadow-lg shadow-primary/20 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" />
            {isLoading ? 'Saving...' : 'Save Tags'}
          </button>
        </div>
      </div>
    </div>
  );
};

